import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { createGroup } from "~/lib/abodemail/generated/api";
import { findOrganizationWithProduct } from "~/lib/entitlements/admin-api";
import { LogtoScopes } from "~/lib/logto/logto";
import {
  requireAuth,
  requireScopes,
  rateLimitFunction,
  createPostHogTrackingMiddleware,
} from "~/lib/middleware";
import { identifyOrganizationGroup } from "~/lib/posthog";
import {
  ApiResponse,
  Organization,
  organizationSchema,
  type OrganizationStatus,
} from "~/lib/schemas";
import { fetchOrganizationData } from "./getOrg";

export const syncOrgToDaasSchema = z.object({
  organizationId: z.string().min(1),
});

export type SyncOrgToDaasRequest = z.infer<typeof syncOrgToDaasSchema>;

// Entitlements classifies an account; DaaS carries a status that means roughly
// the same thing on the rows this portal already renders. An evaluation account
// must not land in DaaS as a full customer, which is what a flat "Active" would
// make it.
function daasStatusForMode(mode: string): OrganizationStatus {
  switch (mode) {
    case "evaluation":
      return "Evaluation";
    case "internal":
      return "Internal";
    default:
      return "Active";
  }
}

// Registers an organization Entitlements already holds into DaaS, under the
// same (Logto) id. Entitlements is the source of truth for which orgs may use
// the portal; this is the one write that lets an org it named hold keys, quotas
// and usage here.
export const syncOrgToDaas = createServerFn({
  method: "POST",
})
  .middleware([
    requireAuth,
    rateLimitFunction,
    requireScopes(LogtoScopes.AdminOrgCrud),
    createPostHogTrackingMiddleware("sync_organization_to_daas"),
  ])
  .validator(syncOrgToDaasSchema)
  .handler(async (ctx): Promise<Organization> => {
    const { organizationId } = ctx.data;

    // Already there — clicking a row twice, or two admins racing, must not turn
    // into a second create attempt.
    const existing = await fetchOrganizationData(organizationId);
    if (existing) {
      return existing;
    }

    // The org is only syncable because Entitlements says it holds the product.
    // Taking the name from there too keeps DaaS's copy the authoritative one
    // rather than whatever the client happened to render.
    const entitlementsOrg = await findOrganizationWithProduct(organizationId);
    if (!entitlementsOrg) {
      throw new Error(
        "This organization does not hold the Data Engine product in Entitlements.",
      );
    }

    const API_MANAGEMENT_BASE_URL =
      process.env.API_MANAGEMENT_BASE_URL || "http://localhost:8000";
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY;

    if (!BFF_API_KEY) {
      throw new Error("API_MANAGEMENT_API_KEY environment variable is required");
    }

    const response = await fetch(
      `${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${BFF_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: entitlementsOrg.name,
          status: daasStatusForMode(entitlementsOrg.mode),
          logto_id: entitlementsOrg.id,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: "Unknown error" }));
      throw new Error(
        errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    const result = (await response.json()) as ApiResponse<Organization>;
    const org = organizationSchema.parse(result.data);

    await identifyOrganizationGroup(org.id, { name: org.name });

    try {
      await createGroup({ id: org.id, name: org.name });
    } catch (abodeMailError) {
      console.error("Failed to create AbodeMail group:", abodeMailError);
      // Don't fail the sync if AbodeMail group creation fails
    }

    return org;
  });
